<script setup lang="ts">
import { invokeMultipartEncoding } from '../js-utils/_shared/form-utils';
import { onMounted, ref, type ComponentInternalInstance } from 'vue';

// declare the component props
const props = defineProps<{
    _rootElement: HTMLElement; // reference to the component root element, passed from the parent component
    rootClass: String; // additional class to add to the root element, passed from the parent component
    label: String; // label for the section
    buttonLabel: String; // label for the button that triggers the file input
    isMultiple: boolean; // whether to allow multiple files to be attached
    uploadFileType: String; // allowed file extensions for upload, comma separated (e.g. "jpg,png,pdf")
    fileTypeInvalidMessage: String; // message to show when the user tries to upload a file with an invalid type
    valueName: String; // name of the hidden input that will store the selected value(s)
    apiUploadFile: String; // API endpoint to upload a file
    apiRemoveFile: String; // API endpoint to remove a file
}>();

// declare the reactive properties
const fileInput = ref<HTMLInputElement | null>(null);

const attachedFiles = ref<Array<{ name: string; path: string; }>>([]); // list of attached files, to be rendered in the template

// declare references to external elements

// other properties

/* -------------------------------------------------------------
    Implement tasks on component mount
 */
onMounted(async (callback: () => void, target?: ComponentInternalInstance | null) => {
    // set a class for the root element (v-app: the div that wraps the component),
    // so we keep the flex layout and the full width of the parent container
    props._rootElement.classList.add(props.rootClass.toString());
});
/* -------------------------------------------------------------
    Event handlers
 */

const addFileClickHandler = function (event: Event) {
    event.stopPropagation();
    fileInput.value!.click();
};

const removeFileClickHandler = async function (event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const path = button.dataset.path;
    if (path) {
        var formObj: Record<string, string | object> = {};
        formObj['filename'] = path;
        let errorMsg = '';
        try {
            const response = await invokeMultipartEncoding(formObj, props.apiRemoveFile.toString());
            if (response.error) {
                errorMsg = 'Error al eliminar el archivo, por favor intentá nuevamente';
            } else if (response.data?.status) {
                errorMsg = response.data.message;
            } else {
                attachedFiles.value = attachedFiles.value.filter((file) => file.path !== path);
            }
        } catch (error) {
            errorMsg = error?.toString() || 'Error interno';
        }
        if (errorMsg) {
            alert(errorMsg);
        }
    }
};

const fileInputChangeHandler = async function (event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
        const allowedTypes = props.uploadFileType.toString().trim().split(',');
        if (input.files[0] && !allowedTypes.includes(input.files[0].type)) {
            alert(props.fileTypeInvalidMessage || "Tipo de archivo seleccionado inválido.");
            input.value = ""; // reset so user can retry
            return;
        }

        // if the file with the same name is already attached, do nothing
        if (attachedFiles.value.filter((file) => file.name === input.files![0]!.name).length > 0) {
            input.value = ""; // reset so user can retry
            return;
        }
        var formObj: Record<string, string | object> = {};
        formObj['attachment'] = input.files[0]!;
        let errorMsg = '';
        try {
            const response = await invokeMultipartEncoding(formObj, props.apiUploadFile.toString());
            if (response.error) {
                errorMsg = 'Error al subir el archivo, por favor intentá nuevamente';
            } else if (response.data?.status) {
                errorMsg = response.data.message;
            } else {
                attachedFiles.value.push({
                    name: input.files[0]!.name,
                    path: response.data!.toString(),
                });
            }
        } catch (error) {
            errorMsg = error?.toString() || 'Error interno';
        }
        if (errorMsg) {
            alert(errorMsg);
        }
    }
};

/* -------------------------------------------------------------
    Functions
 */
</script>

<template>
    <div class="uploaded-files">
        <div class="title">
            <label>{{ $props.label }}</label>
            <button type="button" @click="addFileClickHandler" id="visibleButton" class="bttn blue js-processing-hide">
                {{ $props.buttonLabel }}
            </button>
        </div>
        <div class="no-files" v-if="attachedFiles.length === 0">No hay archivos adjuntos seleccionados.</div>
        <ul>
            <li v-for="file in attachedFiles" :key="file.path">
                <span class="file-name">
                    {{ file.name }}
                </span>

                <button class="bttn pink" type="button" @click="removeFileClickHandler" :data-path="file.path">
                    <i class="icon-close"></i>
                </button>
            </li>
        </ul>
        <input :accept="$props.uploadFileType?.toString()" type="file" ref="fileInput" @change="fileInputChangeHandler"
            style="display: none" />

        <input type="hidden" :name="$props.valueName.toString()"
            :value="attachedFiles.map((file) => file.name + '=' + file.path).join(',')" />
    </div>
</template>
